import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, BookOpen, LogOut, LayoutDashboard, ShieldCheck, Circle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { RoleSwitcher, AdminNotificationBell } from "@/components/NavExtras";

const NavLink = ({ to, children }) => {
  const loc = useLocation();
  const active = loc.pathname === to;
  return (
    <Link
      to={to}
      data-testid={`nav-${to.replace(/\//g, "-") || "home"}`}
      className={`text-sm font-semibold transition-colors ${
        active ? "text-[#D92D20]" : "text-[#111827] hover:text-[#D92D20]"
      }`}
    >
      {children}
    </Link>
  );
};

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [online, setOnline] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/api/widgets/online-users")
      .then(({ data }) => { if (!cancelled) setOnline(data.count); })
      .catch(() => { /* widget is non-critical */ });
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#E5E7EB]">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
          <img src="/tmo-logo.png" alt="TeachMeOnline.ca" className="h-11 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          <NavLink to="/courses">Courses</NavLink>
          <NavLink to="/services">Services</NavLink>
          <NavLink to="/practice">Free Practice Tests</NavLink>
          <NavLink to={user ? "/tutor" : "/tutor-landing"}>Tutoring</NavLink>
          <NavLink to="/calgary-nw">Learning Centre</NavLink>
          <NavLink to="/pricing">Pricing</NavLink>
          <NavLink to="/about">About</NavLink>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {online != null && (
            <span data-testid="nav-online-users"
              className="hidden lg:inline-flex items-center gap-1.5 text-xs text-[#064E3B] bg-[#D1FAE5] px-2.5 py-1 rounded-full font-semibold">
              <Circle className="w-2 h-2 fill-[#10B981] text-[#10B981] animate-pulse" />
              {online} learning now
            </span>
          )}
          {!user && (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm font-semibold text-[#111827] hover:text-[#D92D20]">Sign in</Link>
              <Link to="/register" data-testid="nav-register" className="btn-primary text-sm">Get Started</Link>
            </>
          )}
          {user && (
            <>
              <AdminNotificationBell />
              <RoleSwitcher />
              {user.role === "admin" ? (
                <Link to="/admin" data-testid="nav-admin" className="flex items-center gap-1 text-sm font-semibold text-[#064E3B] hover:text-[#047857]">
                  <ShieldCheck className="w-4 h-4" /> Admin
                </Link>
              ) : user.role === "teacher" ? (
                <Link to="/teacher/dashboard" data-testid="nav-teacher-dash" className="flex items-center gap-1 text-sm font-semibold text-[#111827] hover:text-[#D92D20]">
                  <LayoutDashboard className="w-4 h-4" /> Teacher
                </Link>
              ) : user.role === "parent" ? (
                <Link to="/parent/dashboard" data-testid="nav-parent-dash" className="flex items-center gap-1 text-sm font-semibold text-[#111827] hover:text-[#D92D20]">
                  <LayoutDashboard className="w-4 h-4" /> Parent
                </Link>
              ) : user.role === "partner" ? (
                <Link to="/partner/dashboard" data-testid="nav-partner-dash" className="flex items-center gap-1 text-sm font-semibold text-[#111827] hover:text-[#D92D20]">
                  <LayoutDashboard className="w-4 h-4" /> Partner
                </Link>
              ) : (
                <Link to="/dashboard" data-testid="nav-dashboard" className="flex items-center gap-1 text-sm font-semibold text-[#111827] hover:text-[#D92D20]">
                  <LayoutDashboard className="w-4 h-4" /> Dashboard
                </Link>
              )}
              <button
                data-testid="nav-logout"
                onClick={() => { logout(); nav("/"); }}
                className="flex items-center gap-1 text-sm font-semibold text-[#4B5563] hover:text-[#D92D20]"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </>
          )}
        </div>

        <button
          data-testid="nav-menu-toggle"
          className="md:hidden p-2 rounded-md border border-[#E5E7EB]"
          onClick={() => setOpen(!open)}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E5E7EB] bg-white">
          <div className="px-6 py-4 flex flex-col gap-4">
            <Link to="/courses" onClick={() => setOpen(false)} data-testid="mnav-courses">Courses</Link>
            <Link to="/services" onClick={() => setOpen(false)} data-testid="mnav-services">Services</Link>
            <Link to="/practice" onClick={() => setOpen(false)} data-testid="mnav-practice">Free Practice Tests</Link>
            <Link to={user ? "/tutor" : "/tutor-landing"} onClick={() => setOpen(false)} data-testid="mnav-tutor">Tutoring</Link>
            <Link to="/calgary-nw" onClick={() => setOpen(false)} data-testid="mnav-centre">Learning Centre</Link>
            <Link to="/pricing" onClick={() => setOpen(false)} data-testid="mnav-pricing">Pricing</Link>
            <Link to="/about" onClick={() => setOpen(false)} data-testid="mnav-about">About</Link>
            <Link to="/contact" onClick={() => setOpen(false)} data-testid="mnav-contact">Contact</Link>
            {!user ? (
              <div className="flex gap-3 pt-2">
                <Link to="/login" onClick={() => setOpen(false)} className="btn-outline text-sm flex-1 text-center" data-testid="mnav-login">Sign in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="btn-primary text-sm flex-1 text-center" data-testid="mnav-register">Get Started</Link>
              </div>
            ) : (
              <div className="flex flex-col gap-3 pt-2">
                <Link to={
                  user.role === "admin" ? "/admin"
                  : user.role === "teacher" ? "/teacher/dashboard"
                  : user.role === "parent" ? "/parent/dashboard"
                  : "/dashboard"
                } onClick={() => setOpen(false)} data-testid="mnav-dash">
                  <BookOpen className="inline w-4 h-4 mr-1" />
                  {user.role === "admin" ? "Admin Dashboard"
                    : user.role === "teacher" ? "Teacher Dashboard"
                    : user.role === "parent" ? "Parent Dashboard"
                    : "Dashboard"}
                </Link>
                <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="text-left" data-testid="mnav-logout">Sign out</button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
